/**
 * RangeBar — scope the whole Mobile Forensics view to a time window.
 *
 * Two ways in, because analysts ask two different kinds of question. The
 * presets answer "what happened lately"; the picker answers "what happened
 * between these two moments", which is the one that matters when a range comes
 * from a warrant, a statement or another piece of evidence — so it takes a
 * date *and* a time, not just a day.
 *
 * The window applies to every tab (chats, web history, notifications), so the
 * whole view describes one period rather than three different ones.
 */

import { CalendarIcon, XIcon } from 'lucide-react'
import { type FC, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import type { CommsRange } from '@/services/api/comms'
import { isRanged, rangeLabel, toIsoOrUndefined, toLocalInput } from './comms-format'

/** Presets in days, relative to now. */
const PRESETS = [1, 7, 30, 90] as const

export const RangeBar: FC<{
  range: CommsRange
  onRange: (range: CommsRange) => void
}> = ({ range, onRange }) => {
  const { t, i18n } = useTranslation()
  const active = isRanged(range)
  const [open, setOpen] = useState(false)
  // Draft values so typing a half-finished date does not re-query on each
  // keystroke; the range is applied on submit.
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')

  // Re-seed the draft whenever the popover opens or a preset changes the
  // range, so the picker always shows what is actually in effect.
  useEffect(() => {
    if (!open) return
    setFrom(toLocalInput(range.from))
    setTo(toLocalInput(range.to))
  }, [open, range.from, range.to])

  const preset = (days: number) => () => {
    const end = new Date()
    const start = new Date(end.getTime() - days * 24 * 3_600_000)
    onRange({ from: start.toISOString(), to: end.toISOString() })
  }

  const apply = () => {
    onRange({ from: toIsoOrUndefined(from), to: toIsoOrUndefined(to) })
    setOpen(false)
  }

  return (
    <div className="flex items-center gap-1.5 max-md:order-3 max-md:flex-wrap">
      {PRESETS.map(days => (
        <Button
          key={days}
          variant="outline"
          size="sm"
          className="h-8 px-2 text-2xs md:h-6"
          onClick={preset(days)}
        >
          {t('comms.range.lastDays', { count: days })}
        </Button>
      ))}

      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant={active ? 'secondary' : 'outline'}
            size="sm"
            className="h-8 gap-1.5 px-2 text-2xs md:h-6"
          >
            <CalendarIcon className="size-3.5" />
            {active ? rangeLabel(range, i18n.language) : t('comms.range.pick')}
          </Button>
        </PopoverTrigger>
        <PopoverContent align="end" className="w-72">
          <form
            className="flex flex-col gap-3"
            onSubmit={event => {
              event.preventDefault()
              apply()
            }}
          >
            <div className="flex flex-col gap-1">
              <label htmlFor="comms-range-from" className="text-2xs text-muted-foreground">
                {t('comms.range.from')}
              </label>
              <Input
                id="comms-range-from"
                type="datetime-local"
                value={from}
                max={to || undefined}
                onChange={event => setFrom(event.target.value)}
                className="h-8 text-xs"
              />
            </div>
            <div className="flex flex-col gap-1">
              <label htmlFor="comms-range-to" className="text-2xs text-muted-foreground">
                {t('comms.range.to')}
              </label>
              <Input
                id="comms-range-to"
                type="datetime-local"
                value={to}
                min={from || undefined}
                onChange={event => setTo(event.target.value)}
                className="h-8 text-xs"
              />
            </div>
            {/* Either end alone is a valid window — "everything after this
                moment" is a question an analyst asks as often as a closed
                interval. */}
            <div className="flex items-center justify-between gap-2">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-7 px-2 text-2xs"
                onClick={() => {
                  setFrom('')
                  setTo('')
                  onRange({})
                  setOpen(false)
                }}
              >
                {t('comms.range.clear')}
              </Button>
              <Button type="submit" size="sm" className="h-7 px-3 text-2xs">
                {t('comms.range.apply')}
              </Button>
            </div>
          </form>
        </PopoverContent>
      </Popover>

      {active && (
        <Button
          variant="ghost"
          size="sm"
          className="size-8 p-0 md:size-6"
          onClick={() => onRange({})}
          aria-label={t('comms.range.clearRange')}
        >
          <XIcon className="size-3.5" />
        </Button>
      )}
    </div>
  )
}
